// Simplified cart utilities - single persistence strategy

const CART_STORAGE_KEY = 'unsound_rags_cart';

// Simple localStorage utilities for anonymous users
export const cartStorage = {
  // Get cart from localStorage
  get: () => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  },

  // Save cart to localStorage
  set: (cartItems) => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      // Silently fail if storage is full
    }
  },

  // Clear cart from localStorage
  clear: () => {
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      // Silently fail
    }
  }
};

// Cart item transformation utilities
export const cartTransformers = {
  // Convert database cart item to app cart item format
  fromDatabase: (dbItem) => ({
    id: dbItem.product_id,
    name: dbItem.products?.name || 'Unknown Product',
    price: `$${dbItem.products?.price || '0.00'}`,
    image: (dbItem.products?.images && dbItem.products.images.length > 0)
      ? dbItem.products.images[0]
      : '/images/placeholder.jpg',
    quantity: dbItem.quantity,
    stock: dbItem.products?.stock_quantity || 0,
    cartItemId: dbItem.id // Keep track of database ID for updates
  }),

  // Convert app cart item to database format
  toDatabase: (appItem) => ({
    product_id: appItem.id,
    quantity: appItem.quantity
  }),

  // Merge localStorage cart with database cart
  mergeCarts: (localCart, dbCart) => {
    const merged = [...localCart];

    // Add database items that aren't in local cart
    dbCart.forEach(dbItem => {
      const appItem = cartTransformers.fromDatabase(dbItem);
      const existingIndex = merged.findIndex(item => item.id === appItem.id);

      if (existingIndex >= 0) {
        // Item exists in both - keep the higher quantity
        merged[existingIndex].quantity = Math.max(merged[existingIndex].quantity, appItem.quantity);
        merged[existingIndex].cartItemId = appItem.cartItemId; // Add database ID
      } else {
        // Item only in database - add it
        merged.push(appItem);
      }
    });

    return merged;
  }
};

// Simplified cart operations - single strategy
export const cartSync = {
  // Load cart: authenticated users get from database, anonymous from localStorage
  loadCart: async (isAuthenticated, userId, database) => {
    if (isAuthenticated && userId) {
      try {
        const { data, error } = await database.getUserCart(userId);
        if (data && !error) {
          return data.map(cartTransformers.fromDatabase);
        }
      } catch (error) {
        // Fall back to localStorage if database fails
      }
    }
    // Anonymous users or fallback: use localStorage
    return cartStorage.get();
  },

  // Handle cart operations with simple persistence
  handleCartOperation: async (
    operation,
    cartItems,
    setCartItems,
    isAuthenticated,
    userId,
    database,
    ...args
  ) => {
    let newCartItems = [...cartItems];

    switch (operation) {
      case 'add':
        const [item] = args;
        const existingIndex = newCartItems.findIndex(cartItem => cartItem.id === item.id);

        // Get stock info from database if available
        let stockInfo = item.stock || 0;
        try {
          const { data: product } = await database.getProductById(item.id);
          if (product && product.stock_quantity !== undefined) {
            stockInfo = product.stock_quantity;
          }
        } catch (error) {
          // Use default stock if query fails
        }

        if (existingIndex >= 0) {
          const newQty = newCartItems[existingIndex].quantity + 1;
          // Prevent exceeding stock
          if (stockInfo > 0 && newQty > stockInfo) {
            newCartItems[existingIndex].quantity = stockInfo;
          } else {
            newCartItems[existingIndex].quantity = newQty;
          }
          newCartItems[existingIndex].stock = stockInfo;
        } else {
          newCartItems.push({ ...item, quantity: 1, stock: stockInfo });
        }

        // Save to database if authenticated
        if (isAuthenticated && userId) {
          try {
            await database.addToCart(userId, item.id, 1);
          } catch (error) {
            // Silently fail, cart still works locally
          }
        }
        break;

      case 'update':
        const [id, requestedQuantity] = args;
        const cartItemForUpdate = cartItems.find(item => item.id === id);
        const maxStock = cartItemForUpdate?.stock || 0;
        
        // Respect stock limit
        const finalQuantity = maxStock > 0 
          ? Math.min(requestedQuantity, maxStock) 
          : requestedQuantity;

        if (finalQuantity <= 0) {
          newCartItems = cartItems.filter(item => item.id !== id);
          if (isAuthenticated && userId) {
            const cartItem = cartItems.find(item => item.id === id);
            if (cartItem?.cartItemId) {
              database.removeFromCart(cartItem.cartItemId).catch(() => {});
            }
          }
        } else {
          newCartItems = cartItems.map(item =>
            item.id === id ? { ...item, quantity: finalQuantity } : item
          );
          if (isAuthenticated && userId) {
            const cartItem = cartItems.find(item => item.id === id);
            if (cartItem?.cartItemId) {
              database.updateCartItem(cartItem.cartItemId, finalQuantity).catch(() => {});
            }
          }
        }
        break;

      case 'remove':
        const [removeId] = args;
        newCartItems = cartItems.filter(item => item.id !== removeId);
        if (isAuthenticated && userId) {
          const cartItem = cartItems.find(item => item.id === removeId);
          if (cartItem?.cartItemId) {
            database.removeFromCart(cartItem.cartItemId).catch(() => {});
          }
        }
        break;

      default:
        return;
    }

    // Update state and persist
    setCartItems(newCartItems);
    cartStorage.set(newCartItems);
  }
};

// eslint-disable-next-line import/no-anonymous-default-export
export default { cartStorage, cartTransformers, cartSync };
